import { Button, Typography } from "@cur8/maneki";
import { useCreateNekoScopeAuthUrl } from "hooks/queries/useCreateNekoScopeAuthUrl";
import { CheckInParams } from "lib/types/checkInParameters";
import { useCallback, useEffect } from "react";
import QRCode from "react-qrcode-logo";
import { paths } from "render/routes/paths";
import { InformationLayout } from "render/ui/layout/InformationLayout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import styles from "./styles.module.sass";

type NekoScopeAuthViewProps = {
  goTo(url: string): void;
} & CheckInParams;
// Pre-signed URL is valid for 5 minutes, this value needs to be less than that
const AUTO_HIDE_DELAY = 240000;
const FAKE_QR_ENTROPY = "0129841902481=24g9182418=4gk190gk190gj8m";

export function NekoScopeAuthView({
  goTo,
  patientId,
  visitId,
}: NekoScopeAuthViewProps) {
  const createNekoScopeAuthUrlMutation = useCreateNekoScopeAuthUrl();

  const goToCheckInComplete = useCallback(() => {
    const url = paths.checkInSuccess.url({ patientId, visitId });
    goTo(url);
  }, [goTo, patientId, visitId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      goToCheckInComplete();
    }, AUTO_HIDE_DELAY);
    return () => {
      clearTimeout(timeout);
    };
  }, [goToCheckInComplete]);

  const getQrCodePayload = () => {
    return JSON.stringify({
      url: createNekoScopeAuthUrlMutation.data,
      pid: patientId,
      vid: visitId,
    });
  };

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            <div className={styles.frame} data-dev={getQrCodePayload()}>
              <QRCode
                value={getQrCodePayload() ?? FAKE_QR_ENTROPY}
                size={500}
                ecLevel="L"
                quietZone={10}
                qrStyle="squares"
                eyeRadius={[
                  { outer: [12, 12, 12, 12], inner: [6, 6, 6, 6] },
                  { outer: [12, 12, 12, 12], inner: [6, 6, 6, 6] },
                  { outer: [12, 12, 12, 12], inner: [6, 6, 6, 6] },
                ]}
              />
            </div>

            <div className={styles.instructions}>
              <Typography variant="title-s" color="subtle">
                Scan the QR code with NekoLens to assign the member to the
                device
              </Typography>
            </div>
          </section>
        }
        buttons={
          <Button
            variant="secondary"
            fillContainer
            onPress={goToCheckInComplete}
          >
            Close
          </Button>
        }
      />
    </PageFrameContent>
  );
}
